import { injectable } from 'tsyringe';
import { authenticatedFetch } from '@/shared/auth/AuthenticatedFetch';

/** The two operator tiers: `admin` = full console (platform-operator), `billing` = billing surfaces only (operator-billing). */
export type OperatorRole = 'admin' | 'billing';

export interface OperatorUser {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    enabled: boolean;
    role: OperatorRole;
    createdAt?: number;
}

export interface InviteOperatorInput {
    email: string;
    firstName?: string;
    lastName?: string;
    role: OperatorRole;
}

async function readError(res: Response, fallback: string): Promise<string> {
    try {
        const body = (await res.json()) as { error?: string };
        if (body.error) return body.error;
    } catch { /* keep fallback */ }
    return fallback;
}

/**
 * Manages the vendor operator accounts (SAAS-32) via the operator-gated auth
 * surface. Creating an operator provisions a user in the operator realm and
 * grants the platform-operator role server-side; the realm enforces TOTP on
 * first login.
 */
@injectable()
export class OperatorUsersService {
    async list(): Promise<OperatorUser[]> {
        const res = await authenticatedFetch('/_auth/api/operator/users');
        if (!res.ok) throw new Error(await readError(res, `Failed to load operators (${res.status})`));
        return (await res.json()) as OperatorUser[];
    }

    async invite(input: InviteOperatorInput): Promise<void> {
        const res = await authenticatedFetch('/_auth/api/operator/users', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(input),
        });
        if (!res.ok) throw new Error(await readError(res, `Failed to invite operator (${res.status})`));
    }

    async setEnabled(id: string, enabled: boolean): Promise<void> {
        const res = await authenticatedFetch(`/_auth/api/operator/users/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ enabled }),
        });
        if (!res.ok) throw new Error(await readError(res, `Failed to update operator (${res.status})`));
    }

    async remove(id: string): Promise<void> {
        const res = await authenticatedFetch(`/_auth/api/operator/users/${encodeURIComponent(id)}`, { method: 'DELETE' });
        if (!res.ok) throw new Error(await readError(res, `Failed to delete operator (${res.status})`));
    }
}
